import React, { Component } from 'react';
import { Button, Form } from 'reactstrap';
import { inject, observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { createForm } from '../../../models/Form';
import globalTranslations from '../../../i18n/globalTranslations';
import FormGroupField from '../../form/FormGroupField';
import { allowEmpty, notBlank } from '../../../util/formValidators';
import { modelOf } from '../../../prop-types';
import { createFormField } from '../../../models/FormField';
import AccountStore from '../../../store/AccountStore';
import Address from '../../../models/Address';
import CheckboxField from '../../form/CheckboxField';
import CountryStore from '../../../store/CountryStore';
import SelectField from '../../form/SelectField';
import FormValidator from '../../../services/FormValidator';

const labels = {
  company: <FormattedMessage {...globalTranslations.companyTitle} />,
  firstname: <FormattedMessage {...globalTranslations.firstNameTitle} />,
  lastname: <FormattedMessage {...globalTranslations.lastNameTitle} />,
  postalCode: <FormattedMessage {...globalTranslations.zipCodeTitle} />,
  streetAddress: (
    <FormattedMessage {...globalTranslations.streetAddressTitle} />
  ),
  city: <FormattedMessage {...globalTranslations.cityTitle} />,
  telephone: <FormattedMessage {...globalTranslations.telephoneTitle} />,
  countryId: <FormattedMessage {...globalTranslations.countryTitle} />,
  setAsPrimary: (
    <FormattedMessage
      id="address.setAsPrimary"
      defaultMessage="Set as a primary address"
    />
  )
};

function createOption(value, label) {
  return {
    value,
    label
  };
}

@observer
export class AddressForm extends Component {
  form = null;
  formName = 'AddressForm';

  constructor(props) {
    super(props);
    const { accountStore, formValidator, address } = this.props;

    this.form = createForm({
      company: createFormField(
        { value: address && address.company },
        accountStore.isRetailer ? [notBlank(labels.company)] : []
      ),
      firstname: createFormField(
        { value: address && address.firstname },
        !accountStore.isRetailer
          ? formValidator.firstName(labels.firstname)
          : formValidator.firstName(labels.firstname).map(allowEmpty)
      ),
      lastname: createFormField(
        { value: address && address.lastname },
        !accountStore.isRetailer
          ? formValidator.lastName(labels.firstname)
          : formValidator.lastName(labels.firstname).map(allowEmpty)
      ),
      streetAddress: createFormField(
        { value: address && address.street_address },
        formValidator.streetAddress(labels.streetAddress)
      ),
      postalCode: createFormField(
        { value: address && address.postal_code },
        formValidator.postalCode(labels.postalCode)
      ),
      city: createFormField(
        { value: address && address.city },
        formValidator.city(labels.city)
      ),
      countryId: createFormField({
        value: address ? address.country_id : accountStore.account.country_id
      }),
      telephone: createFormField(
        { value: address && address.telephone },
        formValidator.telephone(labels.telephone).map(allowEmpty)
      ),
      setAsPrimary: createFormField({
        value: address && address.is_primary ? '1' : ''
      })
    });
  }

  handleSubmit = (e) => {
    const { onSubmit } = this.props;

    e.preventDefault();
    this.form.validate();
    if (this.form.valid) {
      onSubmit(this.form.getFieldValues());
    }
  };

  field = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <FormGroupField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          {...props}
        />
      )
    );
  };

  checkbox = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <CheckboxField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          {...props}
        />
      )
    );
  };

  renderCountrySelect = () => {
    const { countryStore } = this.props;

    if (countryStore.countries.length <= 1) {
      return null;
    }

    const fieldName = 'countryId';
    const field = this.form.fields.get(fieldName);

    return (
      field.active && (
        <SelectField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          options={countryStore.countries.map((country) =>
            createOption(String(country.id), country.name)
          )}
        />
      )
    );
  };

  render() {
    const { loading, accountStore, buttonText } = this.props;
    return (
      <Form className={this.formName} noValidate onSubmit={this.handleSubmit}>
        {this.field('firstname', {
          autoComplete: 'given-name',
          required: !accountStore.isRetailer
        })}
        {this.field('lastname', {
          autoComplete: 'family-name',
          required: !accountStore.isRetailer
        })}
        {this.field('company', {
          autoComplete: 'organization',
          required: accountStore.isRetailer
        })}
        {this.field('streetAddress', {
          autoComplete: 'street-address',
          required: true
        })}
        {this.field('postalCode', {
          autoComplete: 'postal-code',
          required: true
        })}
        {this.field('city', { autoComplete: 'address-level2', required: true })}
        {this.renderCountrySelect()}
        {this.field('telephone', { type: 'tel', autoComplete: 'tel-national' })}
        {this.checkbox('setAsPrimary', {
          options: [createOption('1', labels.setAsPrimary)]
        })}
        <Button color="primary" disabled={this.form.valid === false || loading}>
          {buttonText}
        </Button>
      </Form>
    );
  }
}

AddressForm.propTypes = {
  loading: PropTypes.bool,
  buttonText: PropTypes.node.isRequired,
  address: modelOf(Address),
  onSubmit: PropTypes.func.isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  formValidator: PropTypes.instanceOf(FormValidator).isRequired
};

export default inject('accountStore', 'countryStore', 'formValidator')(
  AddressForm
);



// WEBPACK FOOTER //
// ./src/components/address/AddressForm/index.jsx